import { useEffect, useState } from "react";
import type { Follow } from "../ui/paging";
import type { Billing, BillingSummary, Invoice, ProcedureCode } from "@dentacore/shared-types";
import {
  INVOICE_STATUS_LABELS,
  PAYMENT_METHOD_LABELS,
  PAYMENT_METHODS,
} from "@dentacore/shared-types";

import {
  addInvoiceLine,
  createInvoice,
  getBilling,
  issueInvoice,
  listProcedureCodes,
  recordPayment,
  voidInvoice,
} from "../api/patients";
import { activeBranch } from "../api/tokens";
import { useBranchTimezone } from "../api/use-branch-timezone";
import { useCan } from "../auth/use-can";
import { useModules } from "../shell/modules-context";
import { Pager, useFitPages } from "../ui/paging";
import { counted, DAY_GAP, DayCard, DayList, groupByDay } from "./by-day";
import styles from "./billing-tab.module.css";
import { formatMoney, minorUnitsPerUnit, parseMoney } from "./money";
import { RequiredMark } from "../ui/required-mark";
import { announceRing } from "../shell/quick-ring";
import { useRingAsk } from "./ring-ask";
import type { RingAsk } from "./ring-ask";

/**
 * The procedure dropdown's **Other** entry (Anthony's QA, item 15).
 *
 * *"we should put other also in the dropdown, this should come by default,
 * because additional cost can be added without any specific treatments (maybe
 * complication or smthng broke)."*
 *
 * The API has always accepted a line with a description and a price and no
 * catalogue row — `addLine` refuses only a line with *neither*. It was the form
 * that insisted on a procedure, so a breakage had no way onto a bill.
 *
 * A sentinel rather than an empty string, because "" already means *nothing
 * chosen* and the two must stay distinguishable: one disables the button, the
 * other opens two required fields. Underscored so it cannot collide with a UUID.
 */
const OTHER_LINE = "__other__";

/**
 * The Billing tab — the mockup's *Billing history* panel and its four cards.
 *
 * **Records, not payments** (working plan §3.1). Nothing on this screen moves
 * money; "Take payment" records that money changed hands at the desk, which is
 * why it asks for an amount and a method rather than a card.
 *
 * ── Where the numbers come from ─────────────────────────────────────────────
 *
 * **Every figure is computed by the API and rendered here**, including the
 * outstanding balance. A screen that re-added the lines itself would be a
 * second implementation of the arithmetic, and the two would disagree the first
 * time either changed — on a patient's bill, which is the worst place for it.
 *
 * **"Send statement" needs a bill selected** (client, 2026-08-28): pick an
 * invoice, the button lights up, and pressing it confirms. The actual send is
 * over WhatsApp, which is Phase 5 and not built — so for now it says "sent" and
 * does nothing else, rather than sitting there dead.
 *
 * ── Calm, since 2026-09-27 ──────────────────────────────────────────────────
 *
 * The three figures and the two actions share one container at the top. The
 * invoices sit in **one container per day** (PF9) and **start closed unless
 * money is still owed on them** (PF10) — a draft starts closed too. Opening one
 * shows everything it always did. An invoice somebody raises or opens stays
 * open, and the list pages to fit the window (PF4).
 */
export function BillingTab({
  patientId,
  ask,
  onAsked,
}: {
  patientId: string;
  /**
   * The quick ring's *Take payment* or *New invoice* (STORY-221): what this
   * tab's own Take payment or New invoice button does.
   */
  ask?: RingAsk<"pay" | "invoice"> | null;
  /** Told once the ask has been honoured, so it is not honoured again. */
  onAsked?: () => void;
}): React.JSX.Element {
  // The branch the shell is scoped to. Its catalogue is what prices a line
  // (ADR-0008), so an invoice cannot be raised without one.
  const activeBranchId = activeBranch.get();
  // What this reader may do here (STORY-166). A control they may not use is not
  // drawn; the server refuses each of these routes regardless.
  const can = useCan();
  // "Send statement" is a WhatsApp piece: shown only with Chatbot (QF-D23, QF-D9).
  const chatbot = useModules().has("CHATBOT");
  const mayCreate = can("billing.invoice.create");
  const mayIssue = can("billing.invoice.issue");
  /** *Void* on an issued invoice (OQ-D4) — the server could, the screen could not. */
  const mayVoid = can("billing.invoice.void");
  const mayPay = can("billing.payment.record");
  const mayOverride = can("billing.price.override");

  const [billing, setBilling] = useState<Billing | null>(null);
  const [catalogue, setCatalogue] = useState<ProcedureCode[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [lineFor, setLineFor] = useState<string | null>(null);
  const [payFor, setPayFor] = useState<string | null>(null);
  const [codeId, setCodeId] = useState("");
  const [override, setOverride] = useState("");
  const [overrideNote, setOverrideNote] = useState("");
  /**
   * The description for an **Other** line (Anthony's QA, item 15).
   *
   * *"we should put other also in the dropdown, this should come by default,
   * because additional cost can be added without any specific treatments (maybe
   * complication or smthng broke)."*
   *
   * The API has always accepted a line with a description and a price and no
   * catalogue row — `addLine` says so in as many words. Only the form insisted
   * on a procedure, so a breakage or a complication had no way onto a bill.
   */
  const [otherLabel, setOtherLabel] = useState("");
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState<string>("CASH");

  // The invoice picked for a statement, and whether "Send statement" was just pressed.
  const [statementFor, setStatementFor] = useState<string | null>(null);
  const [statementSent, setStatementSent] = useState(false);

  /** Invoices opened or closed by hand; the rest follow PF10's default. */
  const [open, setOpen] = useState<ReadonlyMap<string, boolean>>(new Map());
  /** The day of the invoice last opened or raised, which the pager keeps on screen. */
  const [follow, setFollow] = useState<Follow | null>(null);

  useEffect(() => {
    const controller = new AbortController();

    setBilling(null);
    setError(null);
    setLineFor(null);
    setPayFor(null);
    setStatementFor(null);
    setStatementSent(false);
    setOpen(new Map());

    getBilling(patientId, controller.signal)
      .then(setBilling)
      .catch((cause: unknown) => {
        if (controller.signal.aborted) return;
        setError(cause instanceof Error ? cause.message : "Could not read this file's billing.");
      });

    return () => controller.abort();
  }, [patientId]);

  useEffect(() => {
    const controller = new AbortController();

    // The catalogue is per branch and ships empty (ADR-0008, Q2), so a failure
    // here is not an error worth showing — it means a line is typed by hand,
    // which is allowed.
    listProcedureCodes(controller.signal)
      .then(setCatalogue)
      .catch(() => setCatalogue([]));

    return () => controller.abort();
  }, [activeBranchId]);

  const apply = (work: () => Promise<unknown>, after?: (result: unknown) => void): void => {
    setBusy(true);
    setError(null);

    work()
      .then(async (result) => {
        const next = await getBilling(patientId);
        after?.(result);
        return next;
      })
      .then((next) => {
        setBilling(next);
        setLineFor(null);
        setPayFor(null);
        setCodeId("");
        setOverride("");
        setOverrideNote("");
        setOtherLabel("");
        setAmount("");
      })
      .catch((cause: unknown) => {
        setError(cause instanceof Error ? cause.message : "That did not save.");
      })
      .finally(() => setBusy(false));
  };

  /**
   * **Each invoice is in the currency it was written in** (QA4-D6), so every
   * figure on one is formatted and parsed in `invoice.currency`. The cards are
   * one figure per currency, never one total across them.
   */
  const summaries = billing?.summaries ?? [];
  const currency = summaries[0]?.currency ?? "USD";
  const money = (cents: number, of: string = currency): string => formatMoney(cents, of);
  const perCurrency = (pick: (s: BillingSummary) => number): string =>
    summaries.map((s) => money(pick(s), s.currency)).join(" · ");

  const toMinorUnits = (typed: string, of: string): number | null => parseMoney(typed, of);

  /**
   * Minor units as a plain number for a text box — `12.50`, not `$12.50`.
   *
   * The amount field is parsed back by `parseMoney`, so what it starts as has
   * to be something `parseMoney` accepts: a currency symbol and a thousands
   * separator are exactly what it refuses. **The exponent is the currency's**,
   * from the shared helper, so a zero-decimal currency pre-fills `1200` rather
   * than `1200.00` (QA #69).
   */
  const wholeUnits = (cents: number, of: string): string => {
    const per = minorUnitsPerUnit(of);
    return per === 1 ? String(cents) : (cents / per).toFixed(Math.log10(per));
  };

  const invoices = billing?.invoices ?? [];

  /** *New invoice* — the button's handler, and the quick ring's (STORY-221). */
  const raiseInvoice = (): void => {
    if (activeBranchId === null) return;
    apply(
      () => createInvoice(patientId, { branchId: activeBranchId }),
      (created) => {
        // **A new invoice opens** — it is a draft, which would
        // otherwise start closed, and it was raised to be filled in.
        const invoice = created as Invoice;
        setOpen((current) => new Map(current).set(invoice.id, true));
        setFollow({ key: dayOfInvoice(invoice).slice(0, 10) });
      },
    );
  };

  /**
   * An invoice's *Take payment* — its button toggles the form, the quick ring
   * only ever opens it.
   */
  const takePayment = (invoice: Invoice, toggle: boolean): void => {
    setFollow({ key: dayOfInvoice(invoice).slice(0, 10) });
    const opening = !toggle || payFor !== invoice.id;
    setLineFor(null);
    setPayFor(opening ? invoice.id : null);
    /**
     * **Filled in, not suggested** (client's manual QA, item
     * 34). The amount box carried the outstanding figure as a
     * *placeholder*, so the form looked complete and was empty
     * — and "Record payment" is disabled while it is empty.
     * The common case is being paid what is owed, so that is
     * what it starts as.
     */
    setAmount(opening ? wholeUnits(invoice.outstandingCents, invoice.currency) : "");
  };

  /**
   * **The ring's Take payment is per invoice, as the button is.** With one
   * invoice owed on, its payment form opens; with several, the ring does not
   * pick one — they are all open already (PF10) and the reader chooses.
   */
  useRingAsk(
    ask,
    billing !== null,
    (asked) => {
      if (busy) return;
      if (asked.kind === "invoice") {
        if (mayCreate) raiseInvoice();
        return;
      }
      if (!mayPay) return;
      const owed = invoices.filter(owes);
      const only = owed.length === 1 ? owed[0] : undefined;
      if (only !== undefined) takePayment(only, false);
      else if (owed.length > 1)
        announceRing(`${String(owed.length)} invoices are owed on. Pick the one being paid.`);
      else announceRing("Nothing is owed on an issued invoice.");
    },
    onAsked,
  );

  const isOpen = (invoice: Invoice): boolean =>
    lineFor === invoice.id || payFor === invoice.id || (open.get(invoice.id) ?? owes(invoice));
  const days = groupByDay(invoices, dayOfInvoice);
  /**
   * **Drafted beside due** (PP-D8): plan work bills onto a draft, owed by
   * nobody until it is issued, so it is in no figure above — said here so it
   * is not forgotten. Per currency, never added across them (QA4-D6).
   */
  const drafted = [
    ...invoices
      .filter((invoice) => invoice.status === "DRAFT" && invoice.totalCents > 0)
      .reduce(
        (sums, invoice) =>
          sums.set(invoice.currency, (sums.get(invoice.currency) ?? 0) + invoice.totalCents),
        new Map<string, number>(),
      ),
  ]
    .map(([of, cents]) => money(cents, of))
    .join(" · ");

  const pages = useFitPages({
    keys: days.map((day) => day.key),
    // A closed invoice is its 52px line; an open one adds a row per line and
    // payment and its foot.
    estimate: (index) =>
      Math.max(
        62,
        (days[index]?.rows ?? []).reduce(
          (sum, invoice) =>
            sum +
            52 +
            (isOpen(invoice)
              ? 60 + 30 * (Math.max(invoice.lines.length, 1) + invoice.payments.length)
              : 0),
          0,
        ),
      ),
    gap: DAY_GAP,
    resetOn: patientId,
    follow,
  });
  const [start, end] = pages.range;
  const invoicesBefore = (index: number): number =>
    days.slice(0, index).reduce((sum, day) => sum + day.rows.length, 0);

  return (
    <div
      className={styles.billing}
      data-testid="billing-tab"
      data-invoice-count={billing === null ? undefined : invoices.length}
    >
      {/*
        **One container for the figures and the two actions**: billed, paid and
        outstanding — warm only when something is owed — with Send statement
        and New invoice at its right.
      */}
      <div className={styles.summary} data-testid="billing-summary">
        {billing !== null && (
          <>
            <SummaryCard label="Total billed" value={perCurrency((s) => s.totalBilledCents)} />
            <SummaryCard label="Total paid" value={perCurrency((s) => s.totalPaidCents)} />
            <SummaryCard
              label="Outstanding"
              value={perCurrency((s) => s.outstandingCents)}
              warm={summaries.some((s) => s.outstandingCents > 0)}
              testId="card-outstanding"
            />
            {drafted !== "" && (
              <SummaryCard label="Drafted, not issued" value={drafted} testId="card-drafted" />
            )}
          </>
        )}
        <div className={styles.summaryActions}>
          {chatbot && statementSent && (
            <span className={styles.sent} data-testid="statement-not-yet">
              Sending statements is not available yet.
            </span>
          )}
          {chatbot && (
            <button
              type="button"
              className={styles.secondary}
              disabled={busy || statementFor === null}
              title={
                statementFor === null
                  ? "Select an invoice below to send its statement"
                  : "Sending statements is not available yet"
              }
              onClick={() => {
                // **The WhatsApp send is not built, and this says so** (QF-D23). It
                // used to say "Statement sent." while sending nothing, which told
                // staff a patient had a statement they never received.
                setStatementSent(true);
                setStatementFor(null);
              }}
              data-testid="send-statement"
            >
              Send statement
            </button>
          )}
          {mayCreate && (
            <button
              type="button"
              className={styles.primary}
              disabled={busy || activeBranchId === null}
              onClick={raiseInvoice}
              data-testid="new-invoice"
            >
              New invoice
            </button>
          )}
        </div>
      </div>

      {summaries.some((s) => s.unreconciledCents > 0) && (
        <p className={styles.unreconciled} data-testid="unreconciled">
          {summaries
            .filter((s) => s.unreconciledCents > 0)
            .map((s) => money(s.unreconciledCents, s.currency))
            .join(" · ")}{" "}
          claimed from an insurer and not settled. Nothing has been moved onto the patient — someone
          needs to decide whether it is theirs.
        </p>
      )}

      <div className={styles.head}>
        <h2 className={styles.title}>Billing history</h2>
        <span className={styles.subtitle}>
          Every invoice raised on this file
          {invoices.length > 0 && ` · ${counted(invoices.length, "invoice")}`}
        </span>
      </div>

      {error !== null && (
        <p className={styles.error} data-testid="billing-error">
          {error}
        </p>
      )}

      {billing === null && error === null && (
        <p className={styles.note} data-testid="billing-loading">
          Reading the file…
        </p>
      )}

      {billing !== null && invoices.length === 0 && (
        <p className={styles.empty} data-testid="billing-empty">
          No invoices raised yet.
        </p>
      )}

      {days.length > 0 && (
        <div className={styles.area} ref={pages.areaRef}>
          <DayList testId="invoice-days">
            {days.slice(start, end).map((day) => (
              <DayCard
                key={day.key}
                dayKey={day.key}
                label={day.label}
                count={counted(day.rows.length, "invoice")}
                measureRef={pages.measure(day.key)}
                flush
              >
                {day.rows.map((invoice) => (
                  <InvoiceRow
                    key={invoice.id}
                    invoice={invoice}
                    open={isOpen(invoice)}
                    selected={statementFor === invoice.id}
                    pickable={chatbot}
                    busy={busy}
                    money={(cents) => money(cents, invoice.currency)}
                    onToggle={() => {
                      setFollow({ key: day.key });
                      setOpen((current) => new Map(current).set(invoice.id, !isOpen(invoice)));
                      if (isOpen(invoice)) {
                        if (lineFor === invoice.id) setLineFor(null);
                        if (payFor === invoice.id) setPayFor(null);
                      }
                    }}
                    onPick={() => {
                      setStatementSent(false);
                      setStatementFor((current) => (current === invoice.id ? null : invoice.id));
                    }}
                    onAddLine={() => {
                      setFollow({ key: day.key });
                      setPayFor(null);
                      setLineFor(lineFor === invoice.id ? null : invoice.id);
                    }}
                    onIssue={() => apply(() => issueInvoice(patientId, invoice.id))}
                    onVoid={() => {
                      // A reason is required and audited (OQ-D4); blank cancels.
                      const reason = window.prompt(
                        `Void ${invoice.number}? It stays on the file, marked void, and its work ` +
                          "can be billed again.\n\nWhy is it being voided?",
                      );
                      if (reason === null || reason.trim() === "") return;
                      apply(() => voidInvoice(patientId, invoice.id, reason.trim()));
                    }}
                    mayAddLine={mayCreate}
                    mayIssue={mayIssue}
                    mayVoid={mayVoid}
                    mayPay={mayPay}
                    onTakePayment={() => takePayment(invoice, true)}
                  >
                    {lineFor === invoice.id && (
                      <LineForm
                        invoice={invoice}
                        catalogue={catalogue}
                        busy={busy}
                        codeId={codeId}
                        setCodeId={setCodeId}
                        override={override}
                        setOverride={setOverride}
                        overrideNote={overrideNote}
                        setOverrideNote={setOverrideNote}
                        otherLabel={otherLabel}
                        setOtherLabel={setOtherLabel}
                        money={(cents) => money(cents, invoice.currency)}
                        mayOverride={mayOverride}
                        onSave={() => {
                          // An untouched price box means "take the catalogue's
                          // figure", which is an omitted field rather than a
                          // number — sending the catalogue price back would
                          // record every line as an override of itself.
                          let unitPriceCents: number | undefined;

                          if (override.trim() !== "") {
                            const cents = toMinorUnits(override, invoice.currency);
                            if (cents === null) {
                              setError("That price is not a number.");
                              return;
                            }
                            unitPriceCents = cents;
                          }

                          /**
                           * **An Other line carries a description and no
                           * code**, the shape `addLine` already documents. The
                           * price is required rather than optional here — there
                           * is no catalogue figure behind it to fall back to —
                           * and it is never an override, so no note is asked.
                           */
                          if (codeId === OTHER_LINE) {
                            if (unitPriceCents === undefined) {
                              setError(
                                "An amount is needed — there is no catalogue price for this.",
                              );
                              return;
                            }

                            apply(() =>
                              addInvoiceLine(patientId, invoice.id, {
                                description: otherLabel.trim(),
                                unitPriceCents,
                              }),
                            );
                            return;
                          }

                          apply(() =>
                            addInvoiceLine(patientId, invoice.id, {
                              procedureCodeId: codeId === "" ? undefined : codeId,
                              ...(unitPriceCents === undefined ? {} : { unitPriceCents }),
                              ...(overrideNote.trim() === ""
                                ? {}
                                : { overrideNote: overrideNote.trim() }),
                            }),
                          );
                        }}
                        onCancel={() => setLineFor(null)}
                      />
                    )}

                    {payFor === invoice.id && (
                      <div className={styles.form} data-testid="payment-form">
                        <label className={styles.field}>
                          <span className={styles.fieldLabel}>
                            Amount
                            <RequiredMark />
                          </span>
                          <input
                            className={styles.input}
                            value={amount}
                            onChange={(event) => setAmount(event.target.value)}
                            inputMode="decimal"
                            placeholder={money(invoice.outstandingCents, invoice.currency)}
                            aria-label="Payment amount"
                            data-testid="payment-amount"
                          />
                        </label>

                        <label className={styles.field}>
                          <span className={styles.fieldLabel}>
                            Method
                            <RequiredMark />
                          </span>
                          <select
                            className={styles.select}
                            value={method}
                            onChange={(event) => setMethod(event.target.value)}
                            data-testid="payment-method"
                          >
                            {PAYMENT_METHODS.map((name) => (
                              <option key={name} value={name}>
                                {PAYMENT_METHOD_LABELS[name]}
                              </option>
                            ))}
                          </select>
                        </label>

                        <button
                          type="button"
                          className={styles.primary}
                          disabled={busy || amount.trim() === ""}
                          onClick={() => {
                            const cents = toMinorUnits(amount, invoice.currency);
                            if (cents === null) {
                              setError("That amount is not a number.");
                              return;
                            }
                            apply(() =>
                              recordPayment(patientId, invoice.id, { amountCents: cents, method }),
                            );
                          }}
                          data-testid="payment-save"
                        >
                          Record payment
                        </button>
                        {amount.trim() === "" && (
                          <span className={styles.needs} data-testid="payment-needs-amount">
                            Enter an amount to record.
                          </span>
                        )}
                        <button
                          type="button"
                          className={styles.secondary}
                          disabled={busy}
                          onClick={() => setPayFor(null)}
                          data-testid="payment-cancel"
                        >
                          Cancel
                        </button>
                      </div>
                    )}
                  </InvoiceRow>
                ))}
              </DayCard>
            ))}
          </DayList>
        </div>
      )}

      <Pager
        page={pages.page}
        count={pages.pages.length}
        onPage={pages.setPage}
        from={invoicesBefore(start) + 1}
        to={invoicesBefore(end)}
        total={invoices.length}
        noun="invoices"
        testId="billing-pager"
      />
    </div>
  );
}

/** Money still owed on it — the one reason an invoice starts open (PF10). */
function owes(invoice: Invoice): boolean {
  return invoice.status !== "DRAFT" && invoice.status !== "VOID" && invoice.outstandingCents > 0;
}

/** The day an invoice belongs to: when it was issued, or raised if it has not been. */
function dayOfInvoice(invoice: Invoice): string {
  return invoice.issuedAt ?? invoice.createdAt;
}

/**
 * One invoice: a line that opens and closes, and its lines, payments and
 * actions when open. The statement pick sits outside the toggle, so choosing a
 * bill for a statement never opens or closes it.
 */
function InvoiceRow({
  invoice,
  open,
  selected,
  pickable,
  busy,
  money,
  onToggle,
  onPick,
  onAddLine,
  onIssue,
  onVoid,
  onTakePayment,
  mayAddLine,
  mayIssue,
  mayVoid,
  mayPay,
  children,
}: {
  invoice: Invoice;
  open: boolean;
  selected: boolean;
  /** Whether invoices can be picked for a statement — only with Chatbot (QF-D23). */
  pickable: boolean;
  busy: boolean;
  money: (cents: number) => string;
  onToggle: () => void;
  onPick: () => void;
  onAddLine: () => void;
  onIssue: () => void;
  onVoid: () => void;
  onTakePayment: () => void;
  mayAddLine: boolean;
  mayIssue: boolean;
  mayVoid: boolean;
  mayPay: boolean;
  children: React.ReactNode;
}): React.JSX.Element {
  const timezone = useBranchTimezone();
  return (
    <article
      className={open ? `${styles.invoice} ${styles.open}` : styles.invoice}
      data-testid="invoice"
      data-invoice-id={invoice.id}
      data-status={invoice.status}
      data-selected={selected ? "true" : undefined}
      data-open={open}
    >
      <div className={styles.invoiceHead}>
        {pickable && (
          <button
            type="button"
            role="radio"
            aria-checked={selected}
            aria-label={`Select ${invoice.number} for a statement`}
            className={styles.pick}
            onClick={onPick}
            data-testid="statement-pick"
          />
        )}
        <button
          type="button"
          className={styles.toggle}
          aria-expanded={open}
          onClick={onToggle}
          data-card=""
          data-testid="invoice-toggle"
        >
          <span className={styles.chevron} aria-hidden="true">
            ›
          </span>
          <span className={styles.number} data-testid="invoice-number">
            {invoice.number}
          </span>
          <span className={styles.what}>
            {invoice.lines.length === 0
              ? "No lines yet"
              : invoice.lines.map((line) => line.description).join(" · ")}
          </span>
          <span className={styles.amount} data-testid="invoice-total">
            {money(invoice.totalCents)}
          </span>
          <span className={styles.statusCell}>
            <span className={styles.status} data-testid="invoice-status">
              {INVOICE_STATUS_LABELS[invoice.status]}
            </span>
          </span>
        </button>
      </div>

      {open && (
        <div className={styles.invoiceBody}>
          {invoice.lines.length === 0 ? (
            <p className={styles.note}>No lines yet.</p>
          ) : (
            <ul className={styles.lines}>
              {invoice.lines.map((line) => (
                <li key={line.id} className={styles.line} data-testid="invoice-line">
                  <span className={styles.lineWhat}>
                    {line.description}
                    {line.fdiNumber === null ? "" : ` · tooth ${String(line.fdiNumber)}`}
                    {line.quantity > 1 ? ` × ${String(line.quantity)}` : ""}
                  </span>

                  {line.priceOverridden && (
                    <span
                      className={styles.overridden}
                      title={
                        `Catalogue ${money(line.cataloguePriceCents ?? 0)}. ` +
                        `Changed by ${line.overriddenByName ?? "someone no longer on file"}: ` +
                        `${line.overrideNote ?? ""}`
                      }
                      data-testid="line-overridden"
                    >
                      Price changed
                    </span>
                  )}

                  <span className={styles.lineAmount}>{money(line.amountCents)}</span>
                </li>
              ))}
            </ul>
          )}

          {invoice.payments.length > 0 && (
            <ul className={styles.lines}>
              {invoice.payments.map((payment) => (
                <li key={payment.id} className={styles.payment} data-testid="invoice-payment">
                  <span className={styles.lineWhat}>
                    {PAYMENT_METHOD_LABELS[payment.method]} ·{" "}
                    {formatDate(payment.receivedAt, timezone)}
                    {payment.reference === null ? "" : ` · ${payment.reference}`}
                  </span>
                  <span className={styles.lineAmount}>−{money(payment.amountCents)}</span>
                </li>
              ))}
            </ul>
          )}

          <div className={styles.invoiceFoot}>
            <span className={styles.outstanding}>
              <span data-testid="invoice-outstanding">
                {money(invoice.outstandingCents)} outstanding
              </span>
              <span className={styles.branch}> · {invoice.branchName}</span>
            </span>

            {mayAddLine && invoice.status !== "PAID" && invoice.status !== "VOID" && (
              <button
                type="button"
                className={styles.secondary}
                disabled={busy}
                onClick={onAddLine}
                data-testid="add-line"
              >
                Add a line
              </button>
            )}

            {mayIssue && invoice.status === "DRAFT" && (
              <button
                type="button"
                className={styles.secondary}
                disabled={busy || invoice.lines.length === 0}
                onClick={onIssue}
                data-testid="issue-invoice"
              >
                Issue
              </button>
            )}

            {/*
              **Void, with a reason** (OQ-D4): an issued invoice with nothing
              paid against it — the server refuses the rest, and says why.
            */}
            {mayVoid && invoice.status === "ISSUED" && invoice.payments.length === 0 && (
              <button
                type="button"
                className={styles.secondary}
                disabled={busy}
                onClick={onVoid}
                data-testid="void-invoice"
              >
                Void…
              </button>
            )}

            {mayPay && invoice.status !== "DRAFT" && invoice.status !== "VOID" && (
              <button
                type="button"
                className={styles.primary}
                disabled={busy || invoice.outstandingCents <= 0}
                onClick={onTakePayment}
                data-testid="take-payment"
              >
                Take payment
              </button>
            )}
          </div>

          {children}
        </div>
      )}
    </article>
  );
}

/** One figure in the summary container — warm only when it is money owed. */
function SummaryCard({
  label,
  value,
  warm = false,
  testId,
}: {
  label: string;
  value: string;
  warm?: boolean;
  testId?: string;
}): React.JSX.Element {
  return (
    <div
      className={warm ? `${styles.card} ${styles.warm}` : styles.card}
      data-testid={testId ?? "summary-card"}
    >
      <div className={styles.cardLabel}>{label}</div>
      <div className={styles.cardValue}>{value}</div>
    </div>
  );
}

/**
 * Adding a line.
 *
 * Picking a catalogue row fills the price in, which is the "arrives pre-filled"
 * half of the story. Typing a different figure is an **override** and the note
 * becomes required — the API refuses one without, and the form says so rather
 * than letting the save fail.
 */
function LineForm(props: {
  invoice: Invoice;
  catalogue: ProcedureCode[];
  busy: boolean;
  codeId: string;
  setCodeId: (value: string) => void;
  override: string;
  setOverride: (value: string) => void;
  overrideNote: string;
  setOverrideNote: (value: string) => void;
  otherLabel: string;
  setOtherLabel: (value: string) => void;
  money: (cents: number) => string;
  /** Without `billing.price.override` a catalogue line keeps its price; an Other line has none to keep. */
  mayOverride: boolean;
  onSave: () => void;
  onCancel: () => void;
}): React.JSX.Element {
  const other = props.codeId === OTHER_LINE;
  const chosen = props.catalogue.find((code) => code.id === props.codeId);
  // An Other line has no catalogue price to depart from, so it is never an
  // override and never asks why the price changed.
  const overriding = !other && props.override.trim() !== "";
  const needsNote = overriding && props.overrideNote.trim() === "";
  const otherIsIncomplete =
    other && (props.otherLabel.trim() === "" || props.override.trim() === "");

  return (
    <div className={styles.form} data-testid="line-form">
      <label className={styles.field}>
        <span className={styles.fieldLabel}>
          Procedure
          <RequiredMark />
        </span>
        <select
          className={styles.select}
          value={props.codeId}
          onChange={(event) => {
            props.setCodeId(event.target.value);
            // A new procedure means a new catalogue price, so any override
            // typed against the previous one is cleared rather than silently
            // re-applied to a different line.
            props.setOverride("");
            props.setOverrideNote("");
          }}
          data-testid="line-procedure"
        >
          <option value="">
            {props.catalogue.length === 0 ? "This branch has no catalogue yet" : "Choose…"}
          </option>
          {props.catalogue.map((code) => (
            <option key={code.id} value={code.id}>
              {code.label} · {props.money(code.defaultPriceCents)}
            </option>
          ))}
          {/*
            **Always offered, catalogue or no catalogue** (Anthony's QA, #15).
            A complication, a breakage, a materials charge — real money that
            answers to no procedure. It is last so it does not compete with the
            catalogue for the common case, and present even when the catalogue
            is empty, which is the day a clinic most needs it.
          */}
          <option value={OTHER_LINE}>Other — describe it and set the amount</option>
        </select>
      </label>

      {other && (
        <label className={styles.field}>
          <span className={styles.fieldLabel}>What it is for</span>
          <input
            className={styles.input}
            value={props.otherLabel}
            onChange={(event) => props.setOtherLabel(event.target.value)}
            placeholder="Replacement of a broken denture tooth"
            aria-label="What this line is for"
            data-testid="line-other-label"
          />
        </label>
      )}

      {(other || (chosen !== undefined && props.mayOverride)) && (
        <label className={styles.field}>
          <span className={styles.fieldLabel}>{other ? "Amount" : "Price"}</span>
          <input
            className={styles.input}
            value={props.override}
            onChange={(event) => props.setOverride(event.target.value)}
            inputMode="decimal"
            placeholder={
              chosen === undefined ? props.money(0) : props.money(chosen.defaultPriceCents)
            }
            aria-label={other ? "Amount" : "Price"}
            data-testid="line-price"
          />
        </label>
      )}

      {chosen !== undefined && !other && !props.mayOverride && (
        <p className={styles.note} data-testid="line-catalogue-price">
          {props.money(chosen.defaultPriceCents)}, the procedure list&rsquo;s price.
        </p>
      )}

      {overriding && (
        <label className={styles.field}>
          <span className={styles.fieldLabel}>Why the change</span>
          <input
            className={styles.input}
            value={props.overrideNote}
            onChange={(event) => props.setOverrideNote(event.target.value)}
            placeholder="The catalogue price is not changed"
            aria-label="Why the price changed"
            data-testid="line-override-note"
          />
        </label>
      )}

      <button
        type="button"
        className={styles.primary}
        disabled={props.busy || props.codeId === "" || needsNote || otherIsIncomplete}
        onClick={props.onSave}
        data-testid="line-save"
      >
        Add line
      </button>
      {props.codeId === "" && (
        <span className={styles.needs} data-testid="line-needs-procedure">
          {props.catalogue.length === 0
            ? "This branch has no procedures yet — add some in System, or choose Other."
            : "Choose a procedure, or Other for something the catalogue does not cover."}
        </span>
      )}
      {otherIsIncomplete && (
        <span className={styles.needs} data-testid="line-needs-other">
          An Other line needs both what it is for and an amount.
        </span>
      )}
      <button
        type="button"
        className={styles.secondary}
        disabled={props.busy}
        onClick={props.onCancel}
        data-testid="line-cancel"
      >
        Cancel
      </button>
    </div>
  );
}

function formatDate(iso: string, timeZone: string): string {
  const date = new Date(iso);
  return Number.isNaN(date.getTime())
    ? iso
    : // The location's day, not the browser's (P2 Q8).
      date.toLocaleDateString(undefined, {
        day: "numeric",
        month: "short",
        year: "numeric",
        timeZone,
      });
}
